import { useState } from "react";
import { toast } from "sonner";
import { Check, Flag, Loader2, MessageCircleQuestion, MessagesSquare, Reply, Send, X } from "lucide-react";
import { AgentAvatar } from "@/components/agent-avatar";
import { QuestionAge } from "@/components/pending-question";
import { Markdown } from "@/components/markdown";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/input";
import { Avatar } from "@/components/ui/misc";
import { api, ApiError } from "@/lib/api";
import { avatarOf } from "@/lib/avatars";
import { waitingLabel } from "@/lib/format";
import { previewText } from "@/lib/preview-text";
import {
  RESPONSE_CHANNEL_LABELS,
  RESPONSE_STAGE_LABELS,
  type AgentWithContext,
  type User,
  type WaitingCard,
  type WaitingReason,
} from "@/lib/types";

type Question = Extract<WaitingReason, { kind: "question" }>;

/**
 * Pinned at the top of a card's sheet while the card waits on the user: every
 * reason it is waiting, each with the one action that ends it — answer the
 * question here, open the reply that is ready, or mark a flag answered. Whatever
 * is done, the shared waiting list is re-read at once, so the card's ring goes
 * the moment it is answered rather than on the next poll.
 */
export function WaitingBlock({
  card,
  agents,
  users,
  handles,
  now,
  onOpenResponse,
  onAnswered,
  onError,
  onOpenAgent,
}: {
  card: WaitingCard;
  agents: AgentWithContext[];
  users: User[];
  /** Agent handles, for mention highlighting in the question text. */
  handles: Set<string>;
  now: number;
  onOpenResponse: (responseId: string) => void;
  /** After any action: re-read the waiting list and the board. */
  onAnswered: () => void;
  onError: (message: string) => void;
  /** Opens the chat the question was asked in, at the question. */
  onOpenAgent?: (agentId: string, messageId: string) => void;
}) {
  return (
    <section
      aria-label="Waiting on your answer"
      className="space-y-2.5 rounded-lg border border-waiting/50 bg-waiting/[0.07] p-3 shadow-[0_0_18px_-10px_var(--waiting)]"
    >
      <p className="flex items-center gap-1.5 text-[11.5px] font-semibold uppercase tracking-wide text-waiting">
        <MessageCircleQuestion className="size-3.5" />
        Needs your answer
        <span className="font-normal normal-case tracking-normal text-muted-foreground">· {waitingLabel(card.since, now)}</span>
      </p>
      {card.reasons.map((reason) =>
        reason.kind === "question" ? (
          <QuestionReason
            key={reason.messageId}
            reason={reason}
            agent={agents.find((candidate) => candidate.id === reason.agentId)}
            user={users.find((candidate) => candidate.id === reason.agentId)}
            handles={handles}
            now={now}
            onAnswered={onAnswered}
            onError={onError}
            onOpenChat={onOpenAgent ? () => onOpenAgent(reason.agentId, reason.messageId) : undefined}
          />
        ) : reason.kind === "reply" ? (
          <div key={reason.responseId} className="flex flex-wrap items-center gap-2 rounded-md bg-card/70 px-3 py-2">
            <Reply className="size-4 shrink-0 text-waiting" />
            <p className="min-w-0 flex-1 text-[13px]">
              Reply to <span className="font-medium">{reason.recipientName}</span> is ready to send
              <span className="block text-[11px] text-muted-foreground">
                {RESPONSE_CHANNEL_LABELS[reason.channel]} · {RESPONSE_STAGE_LABELS[reason.stage]} · {waitingLabel(reason.since, now)}
              </span>
            </p>
            <Button size="xs" variant="outline" onClick={() => onOpenResponse(reason.responseId)}>
              Open reply
            </Button>
          </div>
        ) : (
          <FlagReason key={`flag-${reason.since}`} taskId={card.taskId} reason={reason} now={now} onAnswered={onAnswered} onError={onError} />
        ),
      )}
    </section>
  );
}

function QuestionReason({
  reason,
  agent,
  user,
  handles,
  now,
  onAnswered,
  onError,
  onOpenChat,
}: {
  reason: Question;
  agent?: AgentWithContext;
  user?: User;
  handles: Set<string>;
  now: number;
  onAnswered: () => void;
  onError: (message: string) => void;
  onOpenChat?: () => void;
}) {
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState<string | null>(null);

  /** Answers through the agent's own respond route, which queues its answer turn. */
  const answer = async (body: string) => {
    const text = body.trim();
    if (!text || sending) return;
    setSending(text);
    try {
      await api.respondToAgent(reason.agentId, reason.messageId, { action: "answer", body: text });
      setReply("");
      toast.success(`Answer sent to ${reason.agentName}`);
      onAnswered();
    } catch (error) {
      onError(error instanceof ApiError ? error.message : "Could not send the answer");
    } finally {
      setSending(null);
    }
  };

  /** "No longer needed": closes it without an answer, as the chat's button does. */
  const dismiss = async () => {
    if (sending) return;
    setSending("\u0000dismiss");
    try {
      await api.respondToAgent(reason.agentId, reason.messageId, { action: "dismiss" });
      toast.success("Marked no longer needed");
      onAnswered();
    } catch (error) {
      onError(error instanceof ApiError ? error.message : "Could not close the question");
    } finally {
      setSending(null);
    }
  };

  return (
    <div className="space-y-2 rounded-md bg-card/70 p-3">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] font-semibold">
        {agent ? <AgentAvatar agent={agent} size="sm" /> : user ? <Avatar {...avatarOf(user)} size="sm" /> : null}
        <span>{reason.agentName}</span>
        <span className="font-normal text-muted-foreground">asks ·</span>
        <QuestionAge since={reason.since} now={now} className="font-normal" />
        {onOpenChat ? (
          <button className="ml-auto inline-flex items-center gap-1 text-[11.5px] font-normal text-primary hover:underline" onClick={onOpenChat}>
            <MessagesSquare className="size-3" /> in the chat
          </button>
        ) : null}
      </p>
      <div className="text-[13.5px] leading-relaxed">
        <Markdown text={reason.body} handles={handles} />
      </div>
      {reason.options.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {reason.options.map((option) => (
            <Button key={option} size="xs" variant="outline" disabled={sending !== null} onClick={() => void answer(option)}>
              {sending === option ? <Loader2 className="animate-spin" /> : null}
              {option}
            </Button>
          ))}
        </div>
      ) : null}
      <Button size="xs" variant="ghost" className="-ml-2" disabled={sending !== null} onClick={() => void dismiss()}>
        {sending === "\u0000dismiss" ? <Loader2 className="animate-spin" /> : <X />} No longer needed
      </Button>
      <div className="flex items-end gap-2">
        <Textarea
          value={reply}
          onChange={(event) => setReply(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              void answer(reply);
            }
          }}
          placeholder={`Answer ${reason.agentName}…`}
          aria-label={`Answer ${reason.agentName}`}
          rows={2}
          className="min-h-[2.75rem] flex-1 resize-none text-[13px]"
        />
        <Button size="sm" disabled={!reply.trim() || sending !== null} onClick={() => void answer(reply)} aria-label="Send answer">
          {sending !== null && sending === reply.trim() ? <Loader2 className="animate-spin" /> : <Send />}
          <span className="hidden sm:inline">Send</span>
        </Button>
      </div>
    </div>
  );
}

function FlagReason({
  taskId,
  reason,
  now,
  onAnswered,
  onError,
}: {
  taskId: string;
  reason: Extract<WaitingReason, { kind: "flag" }>;
  now: number;
  onAnswered: () => void;
  onError: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const clear = async () => {
    setBusy(true);
    try {
      await api.updateTask(taskId, { waitingOn: null });
      toast.success("Marked answered");
      onAnswered();
    } catch (error) {
      onError(error instanceof ApiError ? error.message : "Could not clear the flag");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-start gap-2 rounded-md bg-card/70 px-3 py-2">
      <Flag className="mt-0.5 size-4 shrink-0 text-waiting" />
      <div className="min-w-0 flex-1 text-[13px]">
        {/* The note is the first 200 characters of an agent's blocker, cut
            wherever they end, so it reads as plain text rather than as markdown
            with half a code span. The blocker itself is in the thread in full. */}
        {reason.note ? <p className="break-words">{previewText(reason.note)}</p> : <p>Flagged as waiting on you.</p>}
        <p className="text-[11px] text-muted-foreground">
          {reason.byName ? `Flagged by ${reason.byName}` : "Flagged"} · {waitingLabel(reason.since, now)}
        </p>
      </div>
      <Button size="xs" variant="outline" disabled={busy} onClick={() => void clear()}>
        {busy ? <Loader2 className="animate-spin" /> : <Check />} Mark answered
      </Button>
    </div>
  );
}
